import { ButtonProps, IconProps, modal, ModalHeaderIconObject, ModalHeaderIconType } from 'bold-ui'
import { ButtonKind } from 'bold-ui/lib/components/Button/Button/ButtonSkins'
import { ModalAutoRenderProps } from 'bold-ui/lib/components/Modal/auto/ModalAuto'
import { ModalDepthLevel, ModalSize } from 'bold-ui/lib/components/Modal/Modal'
import { composeHandlers } from 'bold-ui/lib/util/react'
import { noop } from 'util/functions'

interface ConfirmConfig {
  size?: ModalSize
  confirmLabel?: string
  cancelLabel?: string
  onCancel?(e: React.SyntheticEvent): any
  onClose?(): any
  onConfirm?(e: React.SyntheticEvent): any
  depthLevel?: ModalDepthLevel
  manageOverflow?: boolean
}

export interface ConfirmConfigDefaultRender extends ConfirmConfig {
  body?: React.ReactNode
  title: React.ReactNode
  subtitle?: React.ReactNode
  type?: ButtonProps['kind']
  icon?: ModalHeaderIconType
}

export interface ConfirmConfigCustomRender extends ConfirmConfigDefaultRender {
  render?(renderProps: ModalAutoRenderProps): React.ReactNode
}

const defaultConfig: ConfirmConfigDefaultRender = {
  body: null,
  title: 'Confirmação',
  size: 'small',
  cancelLabel: 'Cancelar',
  confirmLabel: 'Confirmar',
  type: 'primary',
}

const icons: { [key in ButtonKind]: IconProps['icon'] } = {
  normal: 'questionMarkOutline',
  primary: 'questionMarkOutline',
  danger: 'exclamationTriangleOutline',
}

export const confirm = (config: ConfirmConfigCustomRender) => {
  const {
    title,
    subtitle,
    body,
    size,
    onCancel,
    onConfirm,
    confirmLabel,
    cancelLabel,
    type,
    icon,
    onClose,
    depthLevel,
    manageOverflow,
    render = () => body,
  } = {
    ...defaultConfig,
    ...config,
  }

  const resolvedType: ButtonKind = type ?? 'primary'
  const resolvedIcon = icon ?? icons[resolvedType]
  const isIconObject = isHeaderIconObject(resolvedIcon)

  return () =>
    new Promise<boolean>((resolve) => {
      const handleResolve = handleResolveWrapper(resolve)

      modal({
        title,
        subtitle,
        icon: isIconObject
          ? resolvedIcon
          : {
              name: resolvedIcon,
              fill: resolvedType,
            },
        size,
        onClose: composeHandlers(onClose ?? noop, handleResolve(false)),
        render,
        depthLevel,
        manageOverflow,
        actions: [
          {
            label: cancelLabel,
            onClick: composeHandlers(onCancel ?? noop, handleResolve(false)),
            'data-testid': 'cancelarAcaoConfirmacao',
          },
          {
            label: confirmLabel,
            kind: resolvedType,
            onClick: composeHandlers(onConfirm ?? noop, handleResolve(true)),
            'data-testid': 'confirmarAcaoConfirmacao',
          },
        ],
      })()
    })
}

function handleResolveWrapper(resolve: (value: boolean | PromiseLike<boolean>) => void) {
  return (confirm: boolean) => () => resolve(confirm)
}

const isHeaderIconObject = (icon: ModalHeaderIconType): icon is ModalHeaderIconObject =>
  typeof icon === 'object' && 'name' in icon
